import { Injectable, Inject } from '@angular/core'
import { registerLocaleData } from '@angular/common'
import { MissingTranslationHandler, TranslateService } from '@ngx-translate/core'
import { Logger } from 'common/log'

import { IdentityService } from 'main/services/identity.service'
import { APIService, BOOTSTRAP_DATA } from 'api/module'

import localeEN from '@angular/common/locales/en-GB'
import localeDE from '@angular/common/locales/de'
import localeFR from '@angular/common/locales/fr'
import localeRU from '@angular/common/locales/ru'
import { Observable, Subject } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { BootstrapData } from 'elements-sdk/src'
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler'

registerLocaleData(localeEN)
registerLocaleData(localeDE)
registerLocaleData(localeFR)
registerLocaleData(localeRU)

let catalog: any = null

// angular-gettext defines a stub as a constant, impossible to override it with e.g. factory
export function gettext (str: string, context?: string): string {
  if (catalog) {
    return catalog.getString(str, null, context)
  } else {
    return str
  }
}

export class CustomMissingTranslationHandler extends MissingTranslationHandler {
  compiler = new TranslateMessageFormatCompiler()

  // eslint-disable-next-line @typescript-eslint/ban-types
  handle (params: { key: string, translateService: TranslateService, interpolateParams?: Object }): any {
    const v = this.compiler.compile(params.key, params.translateService.currentLang)
    if (typeof v === 'string') {
      return v
    }
    return v(params.interpolateParams ?? {})
  }
}

@Injectable({ providedIn: 'root' })
export class LocaleService {
  logger = new Logger(__filename)

  static readonly allLocales = ['en', 'de', 'fr', 'ru']

  get localeChanged$ (): Observable<string> {
    return this.localeChanged.pipe(distinctUntilChanged())
  }

  get catalogChanged$ (): Observable<Record<string, string|undefined>> {
    return this.catalogChanged.pipe(distinctUntilChanged())
  }

  readonly allLanguages: { code: string, name: string }[]

  private locale = 'en'
  private catalog: Record<string, string|undefined>
  private localeChanged = new Subject<string>()
  private catalogChanged = new Subject<Record<string, string|undefined>>()

  constructor (
    private api: APIService,
    private identity: IdentityService,
    private translate: TranslateService,
    @Inject(BOOTSTRAP_DATA) public bootstrapData: BootstrapData,
  ) {
    identity.identityUpdated$.subscribe(() => this.refresh())
    this.refresh()

    this.allLanguages = [
      {
        code: 'en',
        name: translate.instant('English'),
      },
      {
        code: 'de',
        name: translate.instant('German'),
      },
      {
        code: 'fr',
        name: translate.instant('French'),
      },
      /* {
        code: 'it',
        name: translate.instant('Italian'),
      },
      {
        code: 'es',
        name: translate.instant('Spanish'),
      }, */
      {
        code: 'ru',
        name: translate.instant('Russian'),
      },
      /* {
        code: 'ar',
        name: translate.instant('Arabic'),
      }, */
    ]
  }

  refresh (): void {
    this.setLocale(
      this.identity.user?.language?.toString() ??
      this.bootstrapData.publicParameters.language ??
      'en',
    )
  }

  async setLocale (lang: string): Promise<void> {
    const strings = (await this.api.private.getLocale({ lang })).strings

    if (!this.translate.langs.includes(lang)) {
      this.translate.addLangs([lang])

      // Filter out legacy interpolated strings
      const filteredStrings = Object.fromEntries(
        Object.entries(strings).filter(e => !e[0].includes('{{')),
      )

      this.translate.setTranslation(lang, filteredStrings)
    }

    this.translate.setDefaultLang(lang)
    this.locale = lang
    this.catalog = strings
    this.localeChanged.next(lang)
    this.logger.log('Setting language to', lang)
    this.catalogChanged.next(strings)
  }

  getLocale (): string {
    return this.locale
  }

  getCatalog (): Record<string, string|undefined> {
    return this.catalog
  }
}

@Injectable({ providedIn: 'root' })
export class AngularJSLocaleService {
  gettextCatalog: any
  amMoment: any
  tmhDynamicLocale: any
  tmhDynamicLocaleCache: any
  private angularLocalesCached = false

  constructor (
    private locale: LocaleService,
    @Inject('$injector') private $injector: any, // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
  ) {
    this.gettextCatalog = $injector.get('gettextCatalog')
    this.amMoment = $injector.get('amMoment')
    this.tmhDynamicLocale = $injector.get('tmhDynamicLocale')
    this.tmhDynamicLocaleCache = $injector.get('tmhDynamicLocaleCache')

    catalog = this.gettextCatalog

    locale.localeChanged$.subscribe(l => this.setLocale(l))
    locale.catalogChanged$.subscribe(strings => this.setCatalog(strings))
  }

  setCatalog (strings: Record<string, string|undefined>): void {
    this.gettextCatalog.setStrings(this.locale.getLocale(), strings)
  }

  setLocale (locale: string): void {
    this.cacheAngularLocales()
    this.gettextCatalog.setCurrentLanguage(locale)
    this.amMoment.changeLocale(locale)
    this.tmhDynamicLocale.set(locale)
  }

  getInjectedLocale (): Promise<any> {
    return this.$injector.get('$locale')
  }

  cacheAngularLocales (): void {
    if (this.angularLocalesCached) {
      return
    }
    this.angularLocalesCached = true

    // Can't loop because of Webpack
    require('angular-i18n/en-gb')
    this.tmhDynamicLocaleCache.put('en', this.getInjectedLocale())
    require('angular-i18n/de')
    this.tmhDynamicLocaleCache.put('de', this.getInjectedLocale())
    require('angular-i18n/fr')
    this.tmhDynamicLocaleCache.put('fr', this.getInjectedLocale())
    require('angular-i18n/ru')
    this.tmhDynamicLocaleCache.put('ru', this.getInjectedLocale())
  }
}
